import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, MapPin, Loader2, Play, Users, Clock } from "lucide-react";
import StoryViewer from "@/components/StoryViewer";

const VenueStoriesPage = ({ user, onLogout }) => {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [storyUsers, setStoryUsers] = useState([]);
  const [venue, setVenue] = useState(null);
  const [totalStories, setTotalStories] = useState(0);
  const [showViewer, setShowViewer] = useState(false);
  const [selectedUserIndex, setSelectedUserIndex] = useState(0);

  useEffect(() => {
    fetchVenueStories();
  }, [venueId]);

  const fetchVenueStories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/stories/venue/${venueId}`);
      setStoryUsers(response.data?.users || []);
      setVenue(response.data?.venue || null);
      setTotalStories(response.data?.totalStories || 0);
    } catch (error) {
      console.error("Error fetching venue stories:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenStory = (index) => {
    setSelectedUserIndex(index);
    setShowViewer(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri
          </Button>
          
          <Card className="bg-gradient-to-r from-emerald-500 to-green-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center">
                  <MapPin className="h-8 w-8" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">{venue?.name || "Halı Saha"}</h1>
                  <p className="text-emerald-100">{venue?.district}, {venue?.city}</p>
                </div>
              </div>
              <div className="flex items-center gap-6 mt-4 pt-4 border-t border-white/20">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  <span>{storyUsers.length} Kişi</span>
                </div>
                <div className="flex items-center gap-2">
                  <Play className="h-5 w-5" />
                  <span>{totalStories} Hikaye</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  <span>Son 24 saat</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Story Users Grid */}
        {storyUsers.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Henüz hikaye yok
              </h3>
              <p className="text-gray-500 mb-4">
                Bu sahada son 24 saat içinde paylaşılan hikaye bulunmuyor.
              </p>
              <Button 
                onClick={() => navigate('/social')}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Keşfete Git
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {storyUsers.map((storyUser, index) => (
              <Card 
                key={storyUser.userId}
                className="cursor-pointer hover:shadow-lg transition-shadow overflow-hidden group"
                onClick={() => handleOpenStory(index)}
              >
                <div className="relative aspect-square">
                  {/* Preview Image */}
                  {storyUser.stories[0]?.mediaType === 'video' ? (
                    <video 
                      src={storyUser.stories[0]?.mediaUrl}
                      className="w-full h-full object-cover"
                      muted
                    />
                  ) : (
                    <img 
                      src={storyUser.stories[0]?.mediaUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  
                  {/* Play Icon */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="bg-white/90 rounded-full p-3">
                      <Play className="h-6 w-6 text-emerald-600" />
                    </div>
                  </div>
                  
                  {/* Story Count */}
                  {storyUser.stories.length > 1 && (
                    <Badge className="absolute top-2 right-2 bg-emerald-600">
                      {storyUser.stories.length}
                    </Badge>
                  )}
                  
                  {/* User Info */}
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="flex items-center gap-2">
                      <Avatar className={`h-8 w-8 border-2 ${storyUser.hasUnviewed ? 'border-emerald-400' : 'border-white'}`}>
                        <AvatarImage src={storyUser.userAvatar} />
                        <AvatarFallback className="bg-emerald-600 text-white text-xs">
                          {storyUser.userName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-white text-sm font-medium truncate">
                        {storyUser.userName}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Story Viewer */}
      <StoryViewer
        open={showViewer}
        onClose={() => setShowViewer(false)}
        users={storyUsers}
        initialUserIndex={selectedUserIndex}
        currentUserId={user?.id}
      />
    </div>
  );
};

export default VenueStoriesPage;
